import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './JobCount.module.css';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { format, parseISO } from 'date-fns';

import { toZonedTime } from 'date-fns-tz';

const TIME_ZONE = 'Asia/Kolkata';

const AdminJobStats = () => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          navigate('/admin/login');
          return;
        }

        const token = await user.getIdToken();

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/jobs/count-by-date`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const sortedStats = response.data.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setStats(sortedStats);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stats:', error);
        navigate('/admin/login');
      }
    };

    fetchStats();
  }, [navigate]);

  // Filter logic
  useEffect(() => {
    let filtered = stats;

    if (selectedDate) {
      filtered = stats.filter(
        (row) => format(parseISO(row.date), 'yyyy-MM-dd') === selectedDate
      );
    } else if (selectedMonth) {
      filtered = stats.filter(
        (row) =>
          format(toZonedTime(parseISO(row.date), TIME_ZONE), 'yyyy-MM') ===
          selectedMonth
      );
    }

    setFilteredStats(filtered);
    setCurrentPage(1);
  }, [stats, selectedMonth, selectedDate]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStats = filteredStats.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(filteredStats.length / itemsPerPage);

  const handleCSVExport = () => {
    const header = ['Date,Pending,Approved,Rejected,Archived'];
    const rows = filteredStats.map(
      (row) =>
        `${row.date},${row.pending},${row.approved},${row.rejected},${row.archived}`
    );
    const csvContent = [...header, ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `job_stats_${selectedMonth || selectedDate}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  const clearFilters = () => {
    setSelectedMonth('');
    setSelectedDate('');
    setCurrentPage(1);
  };

  return (
    <div className="container mt-4">
      <h2>📊 Job Stats by Date</h2>

      {loading ? (
        <p>Loading stats...</p>
      ) : (
        <>
          <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
            <div>
              <label className="form-label mb-0">📅 Filter by Month:</label>
              <input
                type="month"
                className="form-control"
                style={{ maxWidth: 200 }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setSelectedDate('');
                }}
              />
            </div>

            <div>
              <label className="form-label mb-0">📆 Filter by Date:</label>
              <input
                type="date"
                className="form-control"
                style={{ maxWidth: 200 }}
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedMonth('');
                }}
              />
            </div>

            {(selectedMonth || selectedDate) && (
              <button className="btn btn-secondary" onClick={clearFilters}>
                ❌ Clear Filters
              </button>
            )}

            <button className="btn btn-success" onClick={handleCSVExport}>
              📄 Export CSV
            </button>

            <button className="btn btn-primary" onClick={handlePrint}>
              🖨️ Print
            </button>
          </div>

          {filteredStats.length === 0 ? (
            <p>No data available for selected filters.</p>
          ) : (
            <>
              <div className={styles.statsTable}>
                <table className="table table-bordered table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Date</th>
                      <th>Pending</th>
                      <th>Approved</th>
                      <th>Rejected</th>
                      <th>Archived</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStats.map((row, index) => (
                      <tr key={index}>
                        <td>{row.date}</td>
                        <td>{row.pending}</td>
                        <td>{row.approved}</td>
                        <td>{row.rejected}</td>
                        <td>{row.archived}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ◀️ Previous
                  </button>

                  <span>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next ▶️
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminJobStats;
